import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import {
  Box,
  Card,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Fragment, KeyboardEvent, useState } from "react";

type ActivityEntry = {
  id: string;
  action: string;
  actor: string;
  date: string;
  time: string;
  changes: Record<string, { old: unknown; new: unknown }>;
};

export const ActivityTable = <T,>({
  entries,
  getRow,
}: {
  entries: T[];
  getRow: (entry: T) => ActivityEntry;
}) => {
  const [page, setPage] = useState(0);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  if (!entries.length)
    return (
      <Typography variant="body2" color="text.secondary" className="p-4">
        No activity yet.
      </Typography>
    );

  const currentPage = Math.min(page, Math.ceil(entries.length / 10) - 1);
  const rows = entries
    .slice(currentPage * 10, currentPage * 10 + 10)
    .map(getRow);
  const pageEnd = Math.min((currentPage + 1) * 10, entries.length);
  const toggleRow = (id: string) =>
    setExpandedRowId((expandedId) => (expandedId === id ? null : id));
  const handleRowKeyDown = (event: KeyboardEvent, id: string) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    toggleRow(id);
  };
  return (
    <>
      <TableContainer component={Card} variant="outlined">
        <Table
          size="small"
          aria-label="Activity history"
          sx={{
            border: 0,
            "& .MuiTableCell-root": {
              px: 2,
              py: "9px",
              border: 0,
              borderBottom: 1,
              borderColor: "divider",
            },
            "& .MuiTableCell-head": {
              height: 44,
              py: 0,
              backgroundColor: "background.paper",
            },
            "& .MuiTableRow-root:last-child .MuiTableCell-body": {
              borderBottom: 0,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {["Activity", "Person", "Date", "Time"].map((label) => (
                <TableCell key={label}>
                  <Typography variant="caption" className="font-semibold">
                    {label}
                  </Typography>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(({ id, action, actor, date, time, changes }) => {
              const hasChanges = Object.keys(changes).length > 0;
              const expanded = hasChanges && expandedRowId === id;
              const detailsId = `activity-changes-${id}`;
              const summaryCellClass = hasChanges ? "!border-b-0" : undefined;
              return (
                <Fragment key={id}>
                  <TableRow
                    aria-controls={hasChanges ? detailsId : undefined}
                    aria-expanded={hasChanges ? expanded : undefined}
                    tabIndex={hasChanges ? 0 : undefined}
                    onClick={hasChanges ? () => toggleRow(id) : undefined}
                    onKeyDown={
                      hasChanges
                        ? (event) => handleRowKeyDown(event, id)
                        : undefined
                    }
                    sx={hasChanges ? { cursor: "pointer" } : undefined}
                  >
                    <TableCell className={summaryCellClass}>{action}</TableCell>
                    <TableCell className={summaryCellClass}>{actor}</TableCell>
                    <TableCell className={summaryCellClass}>{date}</TableCell>
                    <TableCell className={summaryCellClass}>{time}</TableCell>
                  </TableRow>
                  {hasChanges && (
                    <TableRow>
                      <TableCell colSpan={4} className="!p-0">
                        <Collapse in={expanded} timeout="auto" unmountOnExit>
                          <Box
                            id={detailsId}
                            component="pre"
                            aria-label={`${action} changes`}
                            className="m-0 overflow-x-auto whitespace-pre-wrap break-words bg-color-bg p-4 text-sm"
                          >
                            {JSON.stringify(changes, null, 2)}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <nav
        aria-label="Activity pagination"
        className="flex items-center justify-center gap-2"
      >
        <IconButton
          aria-label="Go to previous page"
          disabled={currentPage === 0}
          onClick={() => setPage(currentPage - 1)}
        >
          <KeyboardArrowLeft />
        </IconButton>
        <Typography variant="body2">
          {currentPage * 10 + 1}–{pageEnd} of {entries.length}
        </Typography>
        <IconButton
          aria-label="Go to next page"
          disabled={(currentPage + 1) * 10 >= entries.length}
          onClick={() => setPage(currentPage + 1)}
        >
          <KeyboardArrowRight />
        </IconButton>
      </nav>
    </>
  );
};
